// src/pages/components/forms/BulkUserImport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../api/axios";
import { createUser } from "../../api/users/user";

const RE = {
  username: /^[a-zA-Z0-9._-]{3,20}$/,
  password: /^(?=.*[a-z])(?=.*\d).{6,64}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  phone: /^(01[016789])[-]?\d{3,4}[-]?\d{4}$/,
};

// 전화번호 포맷: 10자리이면서 '1'로 시작하면 010 보정, 하이픈 자동
const formatPhoneKR = (raw) => {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("1")) {
    digits = "0" + digits;
  }
  if (digits.length > 11) digits = digits.slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

const EMAIL_DOMAINS = [
  "gmail.com",
  "naver.com",
  "daum.net",
  "kakao.com",
  "outlook.com",
  "직접입력",
];

const COLS = [
  "아이디",
  "비밀번호",
  "이메일",
  "전화번호",
  "이름",
  "성",
  "회사명",
  "부서명",
  "직위명",
];

const EMPTY_ROW = {
  username: "",
  password: "",
  email: "",
  phone_number: "",
  first_name: "",
  last_name: "",
  company_id: "",
  department_id: "",
  position_id: "",
  _checked: true,
  _error: "",
};

export default function BulkUserImport() {
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  const [rows, setRows] = useState([]);
  const [bulkEmailDomain, setBulkEmailDomain] = useState(EMAIL_DOMAINS[0]);
  const [bulkEmailDomainCustom, setBulkEmailDomainCustom] = useState("");

  const [bulkCompanyId, setBulkCompanyId] = useState("");
  const [bulkDepartmentId, setBulkDepartmentId] = useState("");
  const [bulkPositionId, setBulkPositionId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ ok: 0, fail: 0, total: 0 });
  const [globalError, setGlobalError] = useState("");

  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, d, p] = await Promise.all([
          api.get("companies/"),
          api.get("departments/"),
          api.get("positions/"),
        ]);
        setCompanies(c.data?.results ?? c.data ?? []);
        setDepartments(d.data?.results ?? d.data ?? []);
        setPositions(p.data?.results ?? p.data ?? []);
      } catch (e) {
        console.error("[LOAD ERROR]", e);
      }
    })();
  }, []);

  /* =========================
   * 헤더 인덱스 만들기
   * ========================= */
  const mkIdx = (headerRow) => {
    const headerRaw = headerRow.map((h) => String(h || "").trim());
    const normalize = (h) =>
      String(h || "")
        .toLowerCase()
        .replace(/\(.*?\)/g, "")
        .replace(/\*/g, "")
        .replace(/\s+/g, "");
    const headerNorm = headerRaw.map(normalize);

    const ALIASES = {
      username: ["username", "아이디", "id", "사용자id"],
      password: ["password", "비밀번호", "passwd", "비번"],
      email: ["email", "이메일", "emaillocal또는전체", "emaillocal또는전체"],
      phone_number: [
        "phonenumber",
        "phone_number",
        "전화번호",
        "휴대폰",
        "전화",
      ],
      first_name: [
        "firstname",
        "first_name",
        "이름",
        "givenname",
        "given_name",
      ],
      last_name: [
        "lastname",
        "last_name",
        "성",
        "성씨",
        "familyname",
        "family_name",
      ],
      company_name: [
        "companyname",
        "company_name",
        "회사명",
        "회사",
        "company",
        "companyid",
        "company_id",
      ],
      department_name: [
        "departmentname",
        "department_name",
        "부서명",
        "부서",
        "department",
        "departmentid",
        "department_id",
      ],
      position_name: [
        "positionname",
        "position_name",
        "직위명",
        "직급",
        "직책",
        "position",
        "positionid",
        "position_id",
      ],
    };

    const findIdx = (keys) => {
      for (const k of keys) {
        const i = headerNorm.indexOf(k);
        if (i !== -1) return i;
      }
      return -1;
    };

    const idx = {
      username: findIdx(ALIASES.username.map((x) => x.toLowerCase())),
      password: findIdx(ALIASES.password.map((x) => x.toLowerCase())),
      email: findIdx(ALIASES.email.map((x) => x.toLowerCase())),
      phone_number: findIdx(ALIASES.phone_number.map((x) => x.toLowerCase())),
      first_name: findIdx(ALIASES.first_name.map((x) => x.toLowerCase())),
      last_name: findIdx(ALIASES.last_name.map((x) => x.toLowerCase())),
      company_name: findIdx(ALIASES.company_name.map((x) => x.toLowerCase())),
      department_name: findIdx(
        ALIASES.department_name.map((x) => x.toLowerCase())
      ),
      position_name: findIdx(ALIASES.position_name.map((x) => x.toLowerCase())),
    };

    return { idx };
  };

  /* =========================
   * 이름 -> ID 매핑
   * ========================= */
  const findCompanyIdByName = (name) => {
    if (!name) return "";
    const n = String(name).trim();
    const found = companies.find((c) => String(c.name).trim() === n);
    if (!found) console.warn("[MAP] company name not found:", n);
    return found ? found.id : "";
  };
  const findDeptIdByName = (companyId, name) => {
    if (!name || !companyId) return "";
    const n = String(name).trim();
    const found = departments.find(
      (d) => d.company === Number(companyId) && String(d.name).trim() === n
    );
    if (!found)
      console.warn("[MAP] department name not found:", { companyId, name: n });
    return found ? found.id : "";
  };
  const findPosIdByName = (companyId, name) => {
    if (!name || !companyId) return "";
    const n = String(name).trim();
    const found = positions.find(
      (p) => p.company_id === Number(companyId) && String(p.name).trim() === n
    );
    if (!found)
      console.warn("[MAP] position name not found:", { companyId, name: n });
    return found ? found.id : "";
  };

  /* =========================
   * 내보내기
   * ========================= */
  const exportWithErrors = () => {
    const header = [...COLS, "error"];
    const body = rows.map((r) => [
      r.username,
      r.password,
      r.email,
      r.phone_number,
      r.first_name,
      r.last_name,
      r.company_id,
      r.department_id,
      r.position_id,
      r._error || "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "users");
    XLSX.writeFile(wb, "users_with_errors.xlsx");
  };

  /* =========================
   * 검증
   * ========================= */
  const validateRow = (item) => {
    const errs = [];
    if (
      !item.username ||
      !item.password ||
      !item.email ||
      !item.phone_number ||
      !item.first_name ||
      !item.last_name ||
      !item.company_id ||
      !item.position_id
    )
      errs.push("필수값 누락");
    if (item.username && !RE.username.test(item.username))
      errs.push("username 형식");
    if (item.password && !RE.password.test(item.password))
      errs.push("password 형식");
    if (item.email && !RE.email.test(item.email)) errs.push("email 형식");
    if (item.phone_number && !RE.phone.test(item.phone_number))
      errs.push("phone 형식");
    return errs.join(", ");
  };

  /* =========================
   * 파일 파싱
   * ========================= */
  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const arr = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      if (!arr.length) {
        setGlobalError("시트가 비어 있습니다.");
        return;
      }
      const { idx } = mkIdx(arr[0]);

      const domainBase = (
        bulkEmailDomain === "직접입력" ? bulkEmailDomainCustom : bulkEmailDomain
      ).trim();

      const getCell = (row, key) => {
        const i = idx[key];
        return i >= 0 ? String(row[i] ?? "").trim() : "";
      };

      const list = arr
        .slice(1)
        .filter(
          (r) => r && r.length && r.some((v) => String(v || "").trim() !== "")
        )
        .map((r) => {
          const rawEmail = getCell(r, "email");
          const hasAt = rawEmail.includes("@");
          const email = hasAt
            ? rawEmail
            : domainBase && rawEmail
            ? `${rawEmail}@${domainBase}`
            : rawEmail;

          const phone = formatPhoneKR(getCell(r, "phone_number"));

          let company_id = findCompanyIdByName(getCell(r, "company_name"));
          let department_id = "";
          let position_id = "";

          if (!company_id && bulkCompanyId) {
            company_id = Number(bulkCompanyId);
          }
          if (company_id) {
            department_id = findDeptIdByName(
              company_id,
              getCell(r, "department_name")
            );
            position_id = findPosIdByName(
              company_id,
              getCell(r, "position_name")
            );
          }
          if (!department_id && bulkDepartmentId) {
            department_id = Number(bulkDepartmentId);
          }
          if (!position_id && bulkPositionId) {
            position_id = Number(bulkPositionId);
          }

          const item = {
            username: getCell(r, "username"),
            password: getCell(r, "password"),
            email,
            phone_number: phone,
            first_name: getCell(r, "first_name"),
            last_name: getCell(r, "last_name"),
            company_id: company_id === "" ? "" : Number(company_id),
            department_id: department_id === "" ? "" : Number(department_id),
            position_id: position_id === "" ? "" : Number(position_id),
          };

          return { ...item, _checked: true, _error: validateRow(item) };
        });

      // username 중복(엑셀 내부)
      const dupes = new Set();
      const seen = new Set();
      list.forEach((r) => {
        const u = (r.username || "").trim();
        if (!u) return;
        seen.has(u) ? dupes.add(u) : seen.add(u);
      });
      const final = list.map((r) =>
        dupes.has((r.username || "").trim())
          ? {
              ...r,
              _error: r._error
                ? `${r._error}, 중복 username(엑셀)`
                : "중복 username(엑셀)",
            }
          : r
      );

      setRows(final);
      setGlobalError("");
    };
    reader.readAsArrayBuffer(file);
  };

  /* =========================
   * 행 조작 / 셀 업데이트
   * ========================= */
  const addRow = () =>
    setRows((prev) => [JSON.parse(JSON.stringify(EMPTY_ROW)), ...prev]);

  const deleteSelected = () =>
    setRows((prev) => prev.filter((r) => !r._checked));

  const toggleAll = (checked) =>
    setRows((prev) => prev.map((r) => ({ ...r, _checked: checked })));

  const onEditableKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const updateCells = (i, patch) => {
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[i], ...patch };

      if ("phone_number" in updated) {
        updated.phone_number = formatPhoneKR(updated.phone_number);
      }

      ["company_id", "department_id", "position_id"].forEach((k) => {
        if (k in updated) {
          const v = updated[k];
          updated[k] = v === "" || v === null ? "" : Number(v);
        }
      });

      updated._error = validateRow(updated);
      next[i] = updated;
      return next;
    });
  };

  /* =========================
   * 템플릿 다운로드
   * ========================= */
  const downloadTemplate = () => {
    const header = COLS;
    const ws = XLSX.utils.aoa_to_sheet([header]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "template");
    XLSX.writeFile(wb, "user_import_template.xlsx");
  };

  /* =========================
   * 제출
   * ========================= */
  const submitSelected = async () => {
    const targets = rows.filter((r) => r._checked);
    if (!targets.length) {
      setGlobalError("선택된 행이 없습니다.");
      return;
    }

    const domainBase = (
      bulkEmailDomain === "직접입력" ? bulkEmailDomainCustom : bulkEmailDomain
    ).trim();

    const normalized = targets.map((r) => {
      const email = r.email.includes("@")
        ? r.email
        : r.email && domainBase
        ? `${r.email}@${domainBase}`
        : r.email;

      const company_id =
        r.company_id !== ""
          ? r.company_id
          : bulkCompanyId
          ? Number(bulkCompanyId)
          : "";
      const department_id =
        r.department_id !== ""
          ? r.department_id
          : bulkDepartmentId
          ? Number(bulkDepartmentId)
          : "";
      const position_id =
        r.position_id !== ""
          ? r.position_id
          : bulkPositionId
          ? Number(bulkPositionId)
          : "";

      return { ...r, email, company_id, department_id, position_id };
    });

    const withErrors = normalized.map((r) => ({
      ...r,
      _error: validateRow(r),
    }));
    const bad = withErrors.filter((r) => r._error);
    if (bad.length) {
      setRows(withErrors);
      setGlobalError(
        "형식 오류/필수값 누락 행이 있습니다. 빨간 오류를 확인하세요."
      );
      return;
    }

    setSubmitting(true);
    setProgress({ ok: 0, fail: 0, total: normalized.length });
    setGlobalError("");

    let ok = 0,
      fail = 0;
    for (const r of normalized) {
      const payload = {
        username: r.username,
        password: r.password,
        email: r.email,
        phone_number: r.phone_number,
        first_name: r.first_name,
        last_name: r.last_name,
        membership: {
          company: Number(r.company_id),
          department: r.department_id === "" ? null : Number(r.department_id),
          position: Number(r.position_id),
          is_primary: true,
        },
      };

      try {
        const resp = await createUser(payload);
        console.log("[SUBMIT][OK]", resp?.data);
        ok++;
        setRows((prev) =>
          prev.map((x) => (x === r ? { ...x, _error: "" } : x))
        );
      } catch (e) {
        fail++;
        const msg = e?.response?.data
          ? JSON.stringify(e.response.data)
          : e.message;
        setRows((prev) =>
          prev.map((x) =>
            x === r
              ? { ...x, _error: (x._error ? x._error + ", " : "") + msg }
              : x
          )
        );
      }
      setProgress({ ok, fail, total: normalized.length });
    }

    setSubmitting(false);
    if (fail === 0) {
      setRows((prev) => prev.filter((r) => !r._checked));
      alert("대량 등록 완료");
    } else {
      setGlobalError(
        "일부 행이 실패했습니다. 각 행의 오류 메시지를 확인하세요. (자세한 내용은 콘솔)"
      );
    }
  };

  /* =========================
   * 렌더링 준비
   * ========================= */
  const bulkCanPickDepartment = !!bulkCompanyId;
  const bulkCanPickPosition = !!bulkDepartmentId;

  const bulkFilteredDepartments = useMemo(() => {
    const cid = Number(bulkCompanyId) || null;
    return departments.filter((d) => (cid ? d.company === cid : false));
  }, [departments, bulkCompanyId]);

  const bulkFilteredPositions = useMemo(() => {
    const cid = Number(bulkCompanyId) || null;
    return positions.filter((p) => (cid ? p.company_id === cid : false));
  }, [positions, bulkCompanyId]);

  // 회사별 부서/직위 맵
  const deptsByCompany = useMemo(() => {
    const map = {};
    for (const d of departments) {
      if (!map[d.company]) map[d.company] = [];
      map[d.company].push(d);
    }
    return map;
  }, [departments]);

  const positionsByCompany = useMemo(() => {
    const map = {};
    for (const p of positions) {
      const key = p.company_id;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [positions]);

  const hasBulkData = rows.length > 0;
  const selectedCount = rows.filter((r) => r._checked).length;

  return (
    <div className="rounded-2xl border p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          className="rounded border px-3 py-1"
          onClick={() => {
            if (fileRef.current) fileRef.current.value = "";
          }}
        >
          파일 초기화
        </button>
        <button className="rounded border px-3 py-1" onClick={downloadTemplate}>
          템플릿 다운로드
        </button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-600">이메일 도메인(일괄):</span>
          <select
            className="rounded border px-4 py-2 text-base"
            value={bulkEmailDomain}
            onChange={(e) => setBulkEmailDomain(e.target.value)}
          >
            {EMAIL_DOMAINS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {bulkEmailDomain === "직접입력" && (
            <input
              className="w-56 rounded border px-4 py-2 text-base"
              placeholder="example.com"
              value={bulkEmailDomainCustom}
              onChange={(e) =>
                setBulkEmailDomainCustom(e.target.value.replace(/\s/g, ""))
              }
            />
          )}
        </div>
      </div>

      {/* 일괄 멤버십 기본값 (유지) */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-600">일괄 멤버십 기본값:</span>

        <select
          className="rounded border px-4 py-2 text-base"
          value={bulkCompanyId}
          onChange={(e) => {
            const v = e.target.value;
            setBulkCompanyId(v);
            setBulkDepartmentId("");
            setBulkPositionId("");
          }}
        >
          <option value="">회사 선택</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="rounded border px-4 py-2 text-base"
          value={bulkDepartmentId}
          onChange={(e) => {
            setBulkDepartmentId(e.target.value);
            setBulkPositionId("");
          }}
          disabled={!bulkCanPickDepartment}
        >
          <option value="">부서 선택</option>
          {bulkFilteredDepartments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          className="rounded border px-4 py-2 text-base"
          value={bulkPositionId}
          onChange={(e) => setBulkPositionId(e.target.value)}
          disabled={!bulkCanPickPosition}
        >
          <option value="">직위 선택</option>
          {bulkFilteredPositions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {globalError && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-600">
          {globalError}
        </div>
      )}

      <div className="overflow-auto">
        {/* table-auto 로 브라우저가 자동 폭 계산, w-full 로 전체 폭 사용 */}
        <table className="min-w-[1100px] w-full table-auto border-collapse text-base">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-3 py-2 text-left">선택</th>
              {COLS.map((c) => (
                <th key={c} className="px-3 py-2 text-left">
                  {c}
                </th>
              ))}
              <th className="px-3 py-2 text-left">error</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="p-3 text-gray-500" colSpan={COLS.length + 2}>
                  데이터가 없습니다. 템플릿을 내려받아 채우거나, 파일을
                  업로드하세요.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="border-b align-top">
                  {/* 선택 */}
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!r._checked}
                      onChange={(e) =>
                        updateCells(i, { _checked: e.target.checked })
                      }
                    />
                  </td>

                  {/* 아이디 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        username: e.currentTarget.innerText.trim(),
                      })
                    }
                  >
                    {r.username}
                  </td>

                  {/* 비밀번호 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        password: e.currentTarget.innerText.trim(),
                      })
                    }
                    title="영문 소문자+숫자 포함 6~64자"
                  >
                    {r.password}
                  </td>

                  {/* 이메일 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        email: e.currentTarget.innerText.trim(),
                      })
                    }
                  >
                    {r.email}
                  </td>

                  {/* 전화번호 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        phone_number: e.currentTarget.innerText.trim(),
                      })
                    }
                    title="예: 010-1234-5678"
                  >
                    {r.phone_number}
                  </td>

                  {/* 이름 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        first_name: e.currentTarget.innerText.trim(),
                      })
                    }
                  >
                    {r.first_name}
                  </td>

                  {/* 성 */}
                  <td
                    className="px-3 py-2 align-top whitespace-nowrap"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onKeyDown={onEditableKeyDown}
                    onBlur={(e) =>
                      updateCells(i, {
                        last_name: e.currentTarget.innerText.trim(),
                      })
                    }
                  >
                    {r.last_name}
                  </td>

                  {/* 회사 */}
                  <td className="px-3 py-2 align-top">
                    <select
                      className={`w-full rounded border px-4 py-2 ${
                        !r.company_id ? "border-amber-400" : ""
                      }`}
                      value={r.company_id}
                      onChange={(e) => {
                        const v =
                          e.target.value === "" ? "" : Number(e.target.value);
                        // 회사가 바뀌면 부서/직위 초기화
                        updateCells(i, {
                          company_id: v,
                          department_id: "",
                          position_id: "",
                        });
                      }}
                    >
                      <option value="">회사 선택</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {!r.company_id && (
                      <div className="mt-1 text-xs text-amber-600">
                        엑셀 회사명이 매핑되지 않았습니다. 회사 선택 후
                        부서/직위를 지정하세요.
                      </div>
                    )}
                  </td>

                  {/* 부서 */}
                  <td className="px-3 py-2 align-top">
                    <select
                      className="w-full rounded border px-4 py-2"
                      value={r.department_id}
                      onChange={(e) => {
                        const v =
                          e.target.value === "" ? "" : Number(e.target.value);
                        updateCells(i, { department_id: v });
                      }}
                      disabled={!r.company_id}
                    >
                      <option value="">
                        {r.company_id
                          ? "부서 선택(선택사항)"
                          : "회사 먼저 선택"}
                      </option>
                      {(deptsByCompany[r.company_id] ?? []).map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 직위 */}
                  <td className="px-3 py-2 align-top">
                    <select
                      className="w-full rounded border px-4 py-2"
                      value={r.position_id}
                      onChange={(e) => {
                        const v =
                          e.target.value === "" ? "" : Number(e.target.value);
                        updateCells(i, { position_id: v });
                      }}
                      disabled={!r.company_id}
                    >
                      <option value="">
                        {r.company_id ? "직위 선택" : "회사 먼저 선택"}
                      </option>
                      {(positionsByCompany[r.company_id] ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 에러 */}
                  <td className="px-3 py-2 align-top">
                    {r._error && (
                      <span className="text-red-600">{r._error}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          선택됨: {selectedCount} / 총 {rows.length}
          {submitting && (
            <span className="ml-3">
              진행률: {progress.ok + progress.fail}/{progress.total} (성공{" "}
              {progress.ok}, 실패 {progress.fail})
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-60"
            onClick={submitSelected}
            disabled={submitting || selectedCount === 0}
          >
            {submitting ? "등록 중..." : "선택 행 등록"}
          </button>
          <button
            className="rounded border px-3 py-1"
            onClick={exportWithErrors}
            disabled={!hasBulkData}
          >
            현재 데이터 내보내기
          </button>
          <button className="rounded border px-3 py-1" onClick={addRow}>
            행 추가
          </button>
          <button
            className="rounded border px-3 py-1"
            onClick={() => toggleAll(true)}
          >
            전체 선택
          </button>
          <button
            className="rounded border px-3 py-1"
            onClick={() => toggleAll(false)}
          >
            전체 해제
          </button>
          <button className="rounded border px-3 py-1" onClick={deleteSelected}>
            선택 삭제
          </button>
        </div>
      </div>
    </div>
  );
}
